import { Check } from '../ui/iconos.js';

/**
 * Una opción de lista (opt de la referencia): fila de 72 px con un círculo que se
 * llena al elegirla.
 *
 * **Radio nativo, visualmente oculto pero en el árbol de accesibilidad** (mismo
 * criterio que `Opciones` del motor de formularios): el navegador resuelve las
 * flechas, el "opción 2 de 4" y la agrupación. El `label` que lo envuelve es el
 * blanco de toque entero. Va dentro de un `fieldset` que pone quien la usa.
 *
 * El círculo sin elegir va en gray-500 y no en el gray-400 de la referencia:
 * 4,76:1 contra 2,56:1, y el contorno de un control tiene que pasar 3:1 (WCAG
 * 1.4.11).
 *
 * La elegida se reconoce por **cuatro cosas, no sólo por el color**: el círculo
 * lleno con tilde, el contorno teal-dark, el fondo teal-100 y el halo. El foco
 * de teclado es un anillo aparte, para que "elegida" y "enfocada" no se
 * confundan.
 */
export function Opcion({
  nombre,
  valor,
  elegida,
  onElegir,
  titulo,
  detalle,
}: {
  /** El `name` del grupo de radios. */
  nombre: string;
  valor: string;
  elegida: boolean;
  onElegir: () => void;
  titulo: string;
  detalle?: string | undefined;
}) {
  return (
    <label
      className={`flex w-full cursor-pointer gap-4 rounded-lg px-5 outline-2 -outline-offset-2 has-focus-visible:ring-3 has-focus-visible:ring-ring has-focus-visible:ring-offset-2 ${
        detalle ? 'min-h-22 items-start py-4' : 'min-h-18 items-center'
      } ${
        elegida
          ? 'bg-accent-soft shadow-halo-teal outline-accent-strong'
          : 'bg-card outline-border hover:outline-brand-soft'
      }`}
    >
      <input
        checked={elegida}
        className="sr-only"
        name={nombre}
        onChange={onElegir}
        type="radio"
        value={valor}
      />
      <span
        aria-hidden="true"
        className={`flex size-7 shrink-0 items-center justify-center rounded-full outline-2 -outline-offset-2 ${
          detalle ? 'mt-0.5' : ''
        } ${elegida ? 'bg-accent-strong text-primary-foreground outline-accent-strong' : 'outline-muted-foreground'}`}
      >
        {elegida ? <Check className="size-4.5" strokeWidth={3} /> : null}
      </span>
      <span className="flex flex-1 flex-col text-xl leading-tight font-bold text-heading">
        {titulo}
        {detalle ? <span className="text-md font-regular text-subtle">{detalle}</span> : null}
      </span>
    </label>
  );
}
